/**
 * Plugin/hook registry for the in-house dva replacement (ENG-4570).
 *
 * Trimmed port of dva-core's Plugin supporting only the hooks this app uses:
 * onError, onStateChange, onReducer, onEffect, extraReducers, extraEnhancers.
 */
import { Reducer } from 'redux'

const HOOKS = [
  'onError',
  'onStateChange',
  'onReducer',
  'onEffect',
  'extraReducers',
  'extraEnhancers',
] as const

type HookKey = typeof HOOKS[number]

export const filterHooks = (obj: Record<string, any>): Partial<Record<HookKey, any>> => {
  const filtered: Partial<Record<HookKey, any>> = {}
  for (const key of Object.keys(obj)) {
    if ((HOOKS as readonly string[]).includes(key)) {
      filtered[key as HookKey] = obj[key]
    }
  }
  return filtered
}

export default class Plugin {
  hooks: Record<HookKey, any[]>

  constructor() {
    this.hooks = {} as Record<HookKey, any[]>
    for (const key of HOOKS) {
      this.hooks[key] = []
    }
  }

  use(plugin: Partial<Record<HookKey, any>>) {
    const { hooks } = this
    for (const key in plugin) {
      if (Object.prototype.hasOwnProperty.call(plugin, key)) {
        if (key === 'extraEnhancers') {
          // last writer wins, matching dva-core
          hooks[key as HookKey] = plugin[key as HookKey]
        } else {
          hooks[key as HookKey].push(plugin[key as HookKey])
        }
      }
    }
  }

  apply(key: 'onError', defaultHandler: (...args: any[]) => void) {
    const fns = this.hooks[key]
    return (...args: any[]) => {
      if (fns.length) {
        fns.forEach((fn) => fn(...args))
      } else {
        defaultHandler(...args)
      }
    }
  }

  get(key: 'extraReducers'): Record<string, Reducer>
  get(key: 'onReducer'): (reducer: Reducer) => Reducer
  get(key: 'onEffect' | 'onError' | 'onStateChange' | 'extraEnhancers'): any[]
  get(key: HookKey): any {
    const { hooks } = this
    if (key === 'extraReducers') {
      return hooks[key].reduce((ret, obj) => ({ ...ret, ...obj }), {})
    }
    if (key === 'onReducer') {
      return (reducer: Reducer) =>
        hooks[key].reduce((wrapped, enhancer) => enhancer(wrapped), reducer)
    }
    return hooks[key]
  }
}
